/**
 * Work Lock Utilities
 * 
 * These utilities manage work locking functionality to prevent team members
 * from starting work after the final report has been generated.
 */

export interface WorkLockData {
  date: string;
  supervisorId: number;
  teamMemberIds: number[];
  lockedAt: string;
  finalReportGenerated: boolean;
}

export interface MemberWorkLockData {
  userId: number;
  date: string;
  lockedBy: number;
  lockedAt: string;
  reason: string;
}

/**
 * Check if work is locked for a specific user on a specific date
 */
export const isWorkLockedForUser = (userId: number, date: string): boolean => {
  try {
    // Check individual user lock
    const memberLockKey = `work_locked_${date}_user_${userId}`;
    const memberLock = localStorage.getItem(memberLockKey);
    
    if (memberLock) {
      const lockData: MemberWorkLockData = JSON.parse(memberLock);
      console.log(`Work is locked for user ${userId} on ${date}:`, lockData);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking work lock:', error);
    return false; // If error, allow work to proceed
  }
};

/**
 * Check if work is locked for a supervisor and their team on a specific date
 */
export const isWorkLockedForSupervisor = (supervisorId: number, date: string): WorkLockData | null => {
  try {
    const workLockKey = `work_locked_${date}_supervisor_${supervisorId}`;
    const lockData = localStorage.getItem(workLockKey);
    
    if (lockData) {
      const parsedData: WorkLockData = JSON.parse(lockData);
      console.log(`Work is locked for supervisor ${supervisorId} team on ${date}:`, parsedData);
      return parsedData;
    }
    
    return null;
  } catch (error) {
    console.error('Error checking supervisor work lock:', error);
    return null;
  }
};

/**
 * Get work lock reason for a user
 */
export const getWorkLockReason = (userId: number, date: string): string | null => {
  try {
    const memberLockKey = `work_locked_${date}_user_${userId}`;
    const memberLock = localStorage.getItem(memberLockKey);
    
    if (memberLock) {
      const lockData: MemberWorkLockData = JSON.parse(memberLock);
      return lockData.reason;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting work lock reason:', error);
    return null;
  }
};

/**
 * Clear work lock for a specific date (admin function)
 */
export const clearWorkLockForDate = (supervisorId: number, date: string): void => {
  try {
    // Clear supervisor lock
    const workLockKey = `work_locked_${date}_supervisor_${supervisorId}`;
    localStorage.removeItem(workLockKey);
    
    // Clear all individual member locks for this date
    // We need to iterate through localStorage to find all matching keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`work_locked_${date}_user_`)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    console.log(`Cleared work locks for date ${date}, supervisor ${supervisorId}`);
  } catch (error) {
    console.error('Error clearing work lock:', error);
  }
};

/**
 * Check if SignalR is connected (for blocking UI interactions)
 */
export const isSignalRConnected = (realTimeState: { isConnected?: boolean }): boolean => {
  return realTimeState?.isConnected === true;
};

/**
 * Get user-friendly lock message
 */
export const getWorkLockMessage = (userId: number, date: string): string => {
  const reason = getWorkLockReason(userId, date);
  
  if (reason) {
    return `Lucrul este blocat pentru această dată. Motiv: ${reason}`;
  }
  
  return 'Lucrul este blocat pentru această dată. Raportul final a fost completat.';
};